import {
  StoichiometryCustomEvalOptions,
  StoichiometryStructuredTableRowMetadata,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {UnrecognizedMoleculeWarning} from 'notebook/tables/excess/Util';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ResolveQueryFn, ResolveTypedValueToTypeFn} from 'notebook/tables/typed-values/TypedValueResolution';

const UNRECOGNIZED_MOLECULE_MESSAGE = 'Unrecognized molecule — create this molecule in the registry.';

/**
 * Whether a stoichiometry row still holds an unrecognized (unregistered) molecule.
 *
 * `canonicalSmiles` is written into the row metadata when a structure is sketched but not yet
 * registered, and it is intentionally *preserved* after the molecule is created (the reaction-scheme
 * sketcher round-trip keys off it). So metadata presence alone no longer means "unrecognized". The
 * authoritative signal is the Compound cell: it holds a STRING placeholder while unrecognized and is
 * converted to a BIOENTITY reference once the molecule is created. A row is therefore only still
 * unrecognized when `canonicalSmiles` is set AND the Compound cell is not yet a BIOENTITY.
 */
export function isRowUnrecognizedMolecule(
  rowMetadata: StoichiometryStructuredTableRowMetadata | null | undefined,
  compoundCell: TypedValue | null | undefined
): boolean {
  if (rowMetadata?.canonicalSmiles == null) {
    return false;
  }
  return compoundCell?.type !== BaseTypedValueType.BIOENTITY;
}

export function unrecognizedMoleculeCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition | null,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: StoichiometryCustomEvalOptions,
  _resolveFromResolutionQuery: ResolveQueryFn,
  _resolveTypedValueToType: ResolveTypedValueToTypeFn
): CustomEvalResult {
  const {rowMetadata} = customEvalOptions;

  if (
    rowMetadata?.canonicalSmiles != null &&
    typedValue?.type === BaseTypedValueType.STRING &&
    typedValue.rawValue != null &&
    typedValue.rawValue.length > 0
  ) {
    return {
      val: typedValue,
      error: new UnrecognizedMoleculeWarning(UNRECOGNIZED_MOLECULE_MESSAGE),
      locked: false,
    };
  }

  return {val: typedValue, error: null, locked: false};
}
